import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowPathIcon,
  BuildingOffice2Icon,
  ExclamationTriangleIcon,
  InboxIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

import {
  useDecideInsurerConnectionRequest,
  useInsurerConnectionRequests,
} from '@/hooks/useBrokerInsurers';
import { getErrorMessage } from '@/services/api';
import { formatDate } from '@/utils';

/**
 * The country admin's queue of brokers waiting to be connected to an insurer —
 * ACROSS EVERY BROKER, not just the one currently on screen.
 *
 * ## Why this exists
 *
 * `request_connection` and the approve/decline actions behind
 * `/brokers/insurer-connection-requests/` shipped together, and the per-broker
 * Manage Insurers table can already decide a request for the broker it is
 * showing. But that only works for an admin who has ALREADY guessed which
 * broker to open. Nothing listed the queue itself, so a broker's request went
 * somewhere no human would ever look unless they happened to walk the broker
 * list one page at a time — and until someone approves, the relation stays
 * absent and the insurer never appears in the broker's own list. A request
 * nobody can see is the same as no request at all.
 *
 * Shaped on `ActivationRequestQueue`
 * (`pages/admin/IntegrationsAdminPage.tsx`), the platform's existing
 * "brokers waiting on an admin" table, with the verdict buttons that one never
 * had.
 *
 * ## What approving does
 *
 * `POST .../approve/` is the ONLY thing on the platform that turns a request
 * into an ACTIVE `BrokerInsurerRelation`, and an ACTIVE relation IS the
 * entitlement (`apps.brokers.access.mapped_policies_for`). A broker has no
 * path to one of its own — `toggle_insurer` refuses to create one — which is
 * the whole point of the request existing.
 *
 * ## Three states, not two
 *
 * Loading, failed and empty are rendered as three different things. Collapsing
 * a failed request into the empty state would tell the admin "no broker is
 * waiting" when the truth is the queue never loaded — an authoritative answer
 * with nothing behind it.
 */

export interface InsurerConnectionRequestsQueueProps {
  /** The broker whose page this is rendered on, if any. Its rows are marked as
   *  "this broker" so the admin can tell them from the rest of the country's
   *  queue; it does NOT filter the list, because the queue's job is to show
   *  the requests nobody has gone looking for. */
  highlightBrokerId?: string;
  /** Called after a verdict lands, so a host page holding its own copy of the
   *  data can reload it. The hook already invalidates the react-query caches;
   *  this is for pages that fetch by hand. */
  onDecided?: () => void;
}

export function InsurerConnectionRequestsQueue({
  highlightBrokerId,
  onDecided,
}: InsurerConnectionRequestsQueueProps) {
  const { data, isLoading, isError, error, refetch } = useInsurerConnectionRequests({
    status: 'pending',
  });
  const decide = useDecideInsurerConnectionRequest();
  const [busyId, setBusyId] = useState<string | null>(null);

  const requests = data ?? [];

  /** The verdict. The decline reason is asked for the same way the per-broker
   *  Manage Insurers table asks for it, so the two surfaces that can decide the
   *  same request behave identically. */
  const handleDecide = async (id: string, decision: 'approve' | 'decline') => {
    let reason = '';
    if (decision === 'decline') {
      reason = window.prompt('Why are you declining this request? (optional)') ?? '';
    }
    setBusyId(id);
    try {
      await decide.mutateAsync({ id, decision, reason });
      toast.success(decision === 'approve' ? 'Connection approved' : 'Request declined');
      onDecided?.();
    } catch (err) {
      // Never report success on a failure: the request is still pending and the
      // admin has to know that.
      toast.error(getErrorMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <section
      aria-labelledby="insurer-connection-queue-heading"
      className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700"
    >
      <div className="flex items-start justify-between gap-4 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
        <div>
          <h2
            id="insurer-connection-queue-heading"
            className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2"
          >
            <span>Connection requests awaiting you</span>
            {requests.length > 0 && (
              <span className="inline-flex items-center rounded-full bg-amber-100 dark:bg-amber-900/30 px-2 py-0.5 text-xs font-medium text-amber-800 dark:text-amber-200">
                {requests.length}
              </span>
            )}
          </h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Every open request in your country, not just this broker&rsquo;s. Approving is what
            creates the connection — a broker cannot make one itself.
          </p>
        </div>
        <button
          type="button"
          onClick={() => refetch()}
          className="shrink-0 inline-flex items-center gap-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700/50"
        >
          <ArrowPathIcon className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-10">
          <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-primary-600" />
        </div>
      ) : isError ? (
        <div role="alert" className="px-6 py-10 text-center">
          <ExclamationTriangleIcon className="mx-auto h-10 w-10 text-red-400" />
          <h3 className="mt-3 text-sm font-semibold text-gray-900 dark:text-gray-100">
            Could not load connection requests
          </h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 max-w-md mx-auto">{getErrorMessage(error)}</p>
          <button
            type="button"
            onClick={() => refetch()}
            className="mt-4 inline-flex items-center gap-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700/50"
          >
            <ArrowPathIcon className="w-4 h-4" />
            Try again
          </button>
        </div>
      ) : requests.length === 0 ? (
        <div className="px-6 py-10 text-center">
          <InboxIcon className="mx-auto h-10 w-10 text-gray-400" />
          <h3 className="mt-3 text-sm font-semibold text-gray-900 dark:text-gray-100">Nothing is waiting</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            No broker in your country has an open request to connect to an insurance company.
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                  Broker
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                  Insurance company
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                  Requested
                </th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                  Verdict
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {requests.map((request) => {
                const working = busyId === request.id;
                const isThisBroker = !!highlightBrokerId && request.broker_id === highlightBrokerId;
                return (
                  <tr
                    key={request.id}
                    className={isThisBroker ? 'bg-amber-50 dark:bg-amber-900/20' : 'hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors'}
                  >
                    <td className="px-6 py-4">
                      <Link
                        to={`/brokers/${request.broker_id}/insurers`}
                        className="text-sm font-medium text-primary-700 dark:text-primary-400 hover:underline"
                      >
                        {request.broker_name || 'Unnamed broker'}
                      </Link>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {request.broker_code}
                        {isThisBroker && (
                          <span className="ml-2 inline-flex items-center rounded-full bg-amber-100 dark:bg-amber-900/30 px-2 py-0.5 text-[11px] font-medium text-amber-800 dark:text-amber-200">
                            This broker
                          </span>
                        )}
                      </p>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <BuildingOffice2Icon className="w-4 h-4 text-gray-400 shrink-0" />
                        <div>
                          <p className="text-sm text-gray-900 dark:text-gray-100">{request.insurer_name}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">{request.insurer_code}</p>
                        </div>
                      </div>
                      {request.note && (
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 max-w-md">
                          &ldquo;{request.note}&rdquo;
                        </p>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                      {request.created_at
                        ? formatDate(request.created_at)
                        : '—'}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="inline-flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => handleDecide(request.id, 'approve')}
                          disabled={working}
                          className="bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded-lg text-sm font-medium disabled:opacity-50"
                        >
                          {working ? 'Working...' : 'Approve'}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDecide(request.id, 'decline')}
                          disabled={working}
                          className="border border-red-300 dark:border-red-800 text-red-700 dark:text-red-400 bg-white dark:bg-gray-800 px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50"
                        >
                          Decline
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export default InsurerConnectionRequestsQueue;
